import React, { useEffect } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { MAIN_BG_COLOR } from '../theme';
import MarketingFooterCta from '../components/MarketingFooterCta';
import { LANDING_FOOTER_CTA } from './productPages';

const PAGE = {
    fontHeading: '"DM Serif Text", serif',
    fontBody: '"Inter", sans-serif',
    sectionPaddingX: '1.25rem',
} as const;

type ResourceItem = {
    id: string;
    title: string;
    description: string;
    href: string;
    external?: boolean;
    Icon: SvgIconComponent;
};

const RESOURCES: ResourceItem[] = [
    {
        id: 'newsletter',
        title: 'Newsletter',
        description:
            'Radar semanal del ecosistema de conversación digital en Chile: rankings, señales y contexto.',
        href: 'https://radar.podscope.co',
        external: true,
        Icon: NewspaperOutlinedIcon,
    },
    {
        id: 'ranking-semanal',
        title: 'Ranking semanal',
        description: 'Top programas y canales por views y actividad, actualizado cada semana.',
        href: '/ranking',
        Icon: BarChartOutlinedIcon,
    },
    {
        id: 'livestreams',
        title: 'Livestreams en vivo',
        description:
            'Sigue viewers concurrentes y chat en tiempo real de los livestreams del ecosistema.',
        href: '/live',
        Icon: LiveTvOutlinedIcon,
    },
];

const Recursos: React.FC = () => {
    useEffect(() => {
        analytics.track(AnalyticsEvent.VIEW_RECURSOS_PAGE);
    }, []);

    return (
        <Box sx={{ width: '100%', background: '#fff', minHeight: '70vh' }}>
            <Box
                sx={{
                    width: '100%',
                    background: MAIN_BG_COLOR,
                    py: { xs: 8, md: 10 },
                    px: { xs: PAGE.sectionPaddingX, md: 4 },
                }}
            >
                <Container maxWidth="lg">
                    <Typography
                        component="h1"
                        sx={{
                            fontFamily: PAGE.fontHeading,
                            fontWeight: 800,
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            lineHeight: 1.15,
                            color: '#fff',
                            mb: 2,
                            maxWidth: 800,
                        }}
                    >
                        Recursos
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: PAGE.fontBody,
                            fontWeight: 400,
                            fontSize: { xs: '1.0625rem', md: '1.125rem' },
                            lineHeight: 1.55,
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 640,
                        }}
                    >
                        Lecturas y herramientas públicas del ecosistema de conversación digital en
                        Chile.
                    </Typography>
                </Container>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    py: { xs: 6, md: 8 },
                    px: { xs: PAGE.sectionPaddingX, md: 4 },
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1fr 1fr',
                                md: '1fr 1fr 1fr',
                            },
                            gap: 2,
                        }}
                    >
                        {RESOURCES.map(({ id, title, description, href, external, Icon }) => (
                            <Box
                                key={id}
                                component={external ? 'a' : Link}
                                {...(external
                                    ? {
                                          href,
                                          target: '_blank',
                                          rel: 'noopener noreferrer',
                                      }
                                    : { to: href })}
                                onClick={() =>
                                    analytics.track(AnalyticsEvent.CLICK_RESOURCE, {
                                        resourceId: id,
                                        resourceTitle: title,
                                        location: 'recursos_page',
                                    })
                                }
                                sx={{
                                    background: '#f4f4f5',
                                    borderRadius: '12px',
                                    p: { xs: 2.5, md: 3 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    textAlign: 'left',
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    transition: 'background 0.15s ease',
                                    '&:hover': {
                                        background: '#ececee',
                                    },
                                }}
                            >
                                <Icon sx={{ color: MAIN_BG_COLOR, fontSize: 28, mb: 2 }} />
                                <Typography
                                    component="h2"
                                    sx={{
                                        fontFamily: PAGE.fontBody,
                                        fontWeight: 700,
                                        fontSize: '1.0625rem',
                                        lineHeight: 1.3,
                                        color: MAIN_BG_COLOR,
                                        mb: 1,
                                    }}
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: PAGE.fontBody,
                                        fontWeight: 400,
                                        fontSize: '0.9375rem',
                                        lineHeight: 1.55,
                                        color: 'rgba(0,0,0,0.55)',
                                        mb: 2,
                                        flex: 1,
                                    }}
                                >
                                    {description}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        fontFamily: PAGE.fontBody,
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        color: MAIN_BG_COLOR,
                                    }}
                                >
                                    Ver
                                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Container>
            </Box>

            <MarketingFooterCta
                title={LANDING_FOOTER_CTA.title}
                ctaLabel={LANDING_FOOTER_CTA.ctaLabel}
                ctaTo={LANDING_FOOTER_CTA.ctaTo}
                analyticsLocation="recursos_footer"
            />
        </Box>
    );
};

export default Recursos;
